/**
 * @graphql cc
 */
import React, { useContext, useEffect, useCallback } from 'react';
import { differenceInDays } from 'date-fns';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex } from '@atlaskit/primitives/compiled';
import { useResizingHeight } from '@atlaskit/motion';
import Button from '@atlaskit/button/new';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary';
import { ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER } from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceTrackerContext,
	ExperienceFailure,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import { HTV2NthUserLearningOpportunityCardsList } from './HTV2NthUserLearningOpportunityCardsList';
import { useNthUserOnboardingActions } from './useNthUserOnboardingActions';
import { HTV2Carousel } from './HTV2Carousel';
import { HTV2StoreContainer } from './useHTV2Store';
import type { NthUserHomeOnboardingTakeoverGetPersonalSpaceKeyQuery as NthUserHomeOnboardingTakeoverGetPersonalSpaceKeyQuery$data } from './__types__/NthUserHomeOnboardingTakeoverGetPersonalSpaceKeyQuery';

const i18n = defineMessages({
	collapse: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user.collapse-expand-button.collapse',
		defaultMessage: 'Collapse onboarding',
		description: 'This is the text shown on a button that collapses the onboarding guides.',
	},
	expand: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user.collapse-expand-button.expand',
		defaultMessage: 'Expand onboarding',
		description:
			'This is the text shown on a button that shows the onboarding guides when they are collapsed.',
	},
});

const GetPersonalSpaceKeyQuery = gql`
	query NthUserHomeOnboardingTakeoverGetPersonalSpaceKeyQuery {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;
const CollapseExpandButton = ({
	isCollapsed,
	toggleIsCollapsed,
}: {
	isCollapsed: boolean;
	toggleIsCollapsed?: () => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Button
			testId="nth-user-home-onboarding-takeover-collapse-expand-button"
			iconBefore={(iconProps) =>
				isCollapsed ? (
					<ChevronDownIcon {...iconProps} size="small" />
				) : (
					<ChevronUpIcon {...iconProps} size="small" />
				)
			}
			appearance="subtle"
			onClick={toggleIsCollapsed}
		>
			{formatMessage(isCollapsed ? i18n.expand : i18n.collapse)}
		</Button>
	);
};

export const NthUserHomeOnboardingTakeover = () => {
	const { actions, complete, loading, errors, isFinished } = useNthUserOnboardingActions();
	const { setOnboardingState } = useSetOnboardingState();
	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS),
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isCollapsed, takeoverSeenAt, hasDismissedCarousel, hasDismissedLearningResources } =
		deserializeState(onboardingStateData);

	useEffect(() => {
		if (!onboardingStateLoading && !takeoverSeenAt) {
			setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.takeoverSeenAt,
				value: new Date().toISOString(),
			});
		}
	}, [onboardingStateLoading, takeoverSeenAt, setOnboardingState]);

	const onboardingPeriodDay = takeoverSeenAt
		? differenceInDays(new Date(), new Date(takeoverSeenAt))
		: 0;

	useEffect(() => {
		if (!onboardingStateLoading && !errors.length) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'NthUserHomeOnboardingTakeover',
					attributes: {
						onboardingPeriodDay,
						isFinished,
					},
				},
			}).fire();
		}
	}, [errors, isFinished, onboardingStateLoading, onboardingPeriodDay, createAnalyticsEvent]);

	const resizingHeight = useResizingHeight();

	const toggleIsCollapsed = useCallback(() => {
		setOnboardingState({
			key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.isCollapsed,
			value: isCollapsed ? 'false' : 'true',
		});
	}, [isCollapsed, setOnboardingState]);

	const dismissCarousel = useCallback(() => {
		setOnboardingState({
			key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasDismissedCarousel,
			value: 'true',
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2CarouselDismiss',
				source: 'homeOnboardingTakeover',
			},
		}).fire();
	}, [setOnboardingState, createAnalyticsEvent]);

	const dismissLearningResources = useCallback(() => {
		setOnboardingState({
			key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasDismissedLearningResources,
			value: 'true',
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2LearningResourcesDismiss',
				source: 'homeOnboardingTakeover',
			},
		}).fire();
	}, [setOnboardingState, createAnalyticsEvent]);

	if (loading || onboardingStateLoading) {
		return null;
	}

	// This error is blocking because if onboarding state fails, then a lot of the functionality will be broken
	if (errors.length) {
		return <HTV2Errors errors={[...errors]} />;
	}

	return (
		<div {...resizingHeight}>
			<HTV2Errors errors={[...errors]} />

			<Flex testId="nth-user-home-onboarding-takeover" direction="column" gap="space.200">
				{!isCollapsed && !hasDismissedCarousel && (
					<HTV2Carousel
						actions={actions}
						complete={complete}
						onboardingPeriodDay={onboardingPeriodDay}
						suppressSorting
						onDismissSection={dismissCarousel}
					/>
				)}
				{isFinished && !hasDismissedCarousel && (
					<Flex justifyContent="center">
						<CollapseExpandButton isCollapsed={isCollapsed} toggleIsCollapsed={toggleIsCollapsed} />
					</Flex>
				)}

				{!hasDismissedLearningResources && (
					<HTV2NthUserLearningOpportunityCardsList
						onboardingPeriodDay={onboardingPeriodDay}
						onDismissSection={dismissLearningResources}
					/>
				)}
			</Flex>
			<ExperienceSuccess name={ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER} />
		</div>
	);
};

const HTV2Errors = ({ errors }: { errors: (undefined | Error | ApolloErrorInterop)[] }) => {
	return (
		<>
			{errors.map((error, index) => {
				markErrorAsHandled(error);
				return error ? (
					<>
						<ErrorDisplay key={`${error.message}-${index}`} error={error} />
						<ExperienceFailure name={ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER} error={error} />
					</>
				) : null;
			})}
		</>
	);
};

export const NthUserHomeOnboardingTakeoverErrorBoundary = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	return (
		<TransparentErrorBoundary
			attribution={Attribution.FLYWHEEL_ACTIVATION}
			onError={(error) => {
				experienceTracker.stopOnError({
					name: ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER,
					error,
				});
			}}
		>
			<HomeOnboardingTakeoverV2Store />
		</TransparentErrorBoundary>
	);
};

const HomeOnboardingTakeoverV2Store = () => {
	const {
		data: personalSpaceKeyData,
		loading: personalSpaceKeyLoading,
		error: personalSpaceKeyError,
	} = useQuery<NthUserHomeOnboardingTakeoverGetPersonalSpaceKeyQuery$data>(
		GetPersonalSpaceKeyQuery,
	);

	if (personalSpaceKeyLoading) {
		return null;
	}

	const personalSpaceKey =
		(personalSpaceKeyData?.user?.confluence?.hasPersonalSpace &&
			personalSpaceKeyData?.user?.confluence?.space?.key) ||
		'';

	return (
		<HTV2StoreContainer takeoverType="nth-user" personalSpaceKey={personalSpaceKey}>
			<HTV2Errors errors={[personalSpaceKeyError]} />
			<NthUserHomeOnboardingTakeover />
		</HTV2StoreContainer>
	);
};
